import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, combineLatest, distinctUntilChanged, filter, fromEvent, map, skip } from 'rxjs';
import { GameStoreDispatcher } from 'src/app/core/state/game/game.dispatcher';

@Injectable({
    providedIn: 'root',
})
export class ResizeService implements OnDestroy {
    private _deviceViewPortWidth = window.innerWidth;
    private _deviceViewPortHeight = window.innerHeight;
    private _resizeSubscription: Subscription;
    private _windowResizeSubscription: Subscription;
    private _resizeTimeout: NodeJS.Timeout;
    private _commonFontSize: BehaviorSubject<string> = new BehaviorSubject('7px');
    private _sopoPopopFontSize: BehaviorSubject<string> = new BehaviorSubject('');
    private _tournamentRegPageFontSize: BehaviorSubject<string> = new BehaviorSubject('');
    private _headerFontSize: BehaviorSubject<string> = new BehaviorSubject('');
    private _bitcoinGameWinnerFontSize: BehaviorSubject<string> = new BehaviorSubject('');
    private _certificateFontSize: BehaviorSubject<string> = new BehaviorSubject('');
    private _sweepstakeOverlayFontSize: BehaviorSubject<string> = new BehaviorSubject('');
    private _sopoToastFontSize: BehaviorSubject<string> = new BehaviorSubject('');
    private _resizeSubject: BehaviorSubject<void> = new BehaviorSubject(null);
    private _manualResizeSubject: BehaviorSubject<void> = new BehaviorSubject(null);

    constructor(
        private gameDispatcher: GameStoreDispatcher
    ) {
        this.initSubscription();
    }

    private initSubscription() {
        this._windowResizeSubscription = fromEvent(window, 'resize').pipe(
            filter((event) => (event.target as Window).innerWidth !== this._deviceViewPortWidth || (event.target as Window).innerHeight !== this._deviceViewPortHeight),
            map(event => (event.target as Window)))
            .subscribe((target) => {
                this._deviceViewPortWidth = target.innerWidth;
                this._deviceViewPortHeight = target.innerHeight;
                if (this._resizeTimeout) clearTimeout(this._resizeTimeout);
                this._resizeTimeout = setTimeout(() => {
                    this._resizeSubject.next();
                }, 200);
            });

        const isBreadCrumb$ = this.gameDispatcher.getState().pipe(map(gameState => gameState.gamesList.length > 0), distinctUntilChanged());

        this._resizeSubscription = combineLatest([this._resizeSubject, this._manualResizeSubject, isBreadCrumb$]).subscribe(([val1, val2, isBreadCrumb]) => {
            this.calculateFontSizes(isBreadCrumb);
        });
    }

    calculateFontSizes(isBreadCrumb: boolean) {
        this.calculateCommonFontSize();
        this.calculatePopUpFontSize();
        this.calculateTournamentRegPageFontSize();
        this.calculateHeaderFontSize();
        this.calculatebitcoinGameWinnerFontSize();
        this.calculateCertificateFontSize();
        this.calculateSweepstakeOveralyFontSize();
        this.calculateSopoToastFontSize();
    }

    private async calculateCommonFontSize() {
        const scale = Math.min(this._deviceViewPortWidth / 1920, this._deviceViewPortHeight / 1080);
        this._commonFontSize.next(`${Math.max(7, 24 * scale)}px`);
    }

    private calculatePopUpFontSize() {
        const width = this._deviceViewPortWidth;
        const fontSize = width >= 1500 ? (width / 110).toFixed(2) : (width / 90).toFixed(2);
        this._sopoPopopFontSize.next(`${fontSize}px`);
    }

    private calculateTournamentRegPageFontSize() {
        const width = this._deviceViewPortWidth;
        const fontSize = width >= 1366 ? '16' : (width / 85).toFixed(2);
        this._tournamentRegPageFontSize.next(`${fontSize}px`);
    }

    private async calculateHeaderFontSize() {
        let divider = 1;
        let width = this._deviceViewPortWidth;
        const height = this._deviceViewPortHeight;
        const element = document.getElementById('header-container');
        if (element) {
            await this.waitForElementSizeNotZero(element);
            const clientRect = element.getBoundingClientRect();
            width = clientRect.width;
        } else {
            width = this._deviceViewPortWidth;
        }
        const aspect = width / height;
        divider = (aspect * 2 * 16) + 24;
        const fontSize = (width / divider).toFixed(2);
        this._headerFontSize.next(`${fontSize}px`);
    }

    private calculatebitcoinGameWinnerFontSize() {
        const width = this._deviceViewPortWidth;
        const fontSize = width >= 1500 ? (width / 110).toFixed(2) : (width / 90).toFixed(2);
        this._bitcoinGameWinnerFontSize.next(`${fontSize}px`);
    }

    private calculateCertificateFontSize() {
        let widthToCalculate = 0;
        let width = this._deviceViewPortWidth;
        let height = this._deviceViewPortHeight;
        const header = document.getElementById('header-container');
        if (header) {
            this.waitForElementSizeNotZero(header);
            const clientRect = header.getBoundingClientRect();
            height = height - clientRect.height;
        }
        if (width / 1.8 > height) {
            widthToCalculate = height * 1.8;
        } else widthToCalculate = height * 1.7;
        const fontSize = widthToCalculate / 55.4;
        this._certificateFontSize.next(`${fontSize}px`);
    }

    private calculateSweepstakeOveralyFontSize() {
        const width = this._deviceViewPortWidth;
        const fontSize = width >= 1500 ? (width / 100).toFixed(2) : (width / 90).toFixed(2);
        this._sweepstakeOverlayFontSize.next(`${fontSize}px`);
    }

    private calculateSopoToastFontSize() {
        const width = this._deviceViewPortWidth;
        const fontSize = width >= 1500 ? (width / 100).toFixed(2) : (width / 80).toFixed(2);
        this._sopoToastFontSize.next(`${fontSize}px`);
    }

    private async waitForElementSizeNotZero(element: HTMLElement): Promise<void> {
        return new Promise((resolve) => {
            const checkSize = () => {
                const { offsetWidth, offsetHeight } = element;
                return offsetWidth > 0 && offsetHeight > 0;
            };

            if (checkSize()) {
                resolve();
                return;
            }

            const observer = new ResizeObserver(() => {
                if (checkSize()) {
                    observer.disconnect();
                    resolve();
                }
            });

            observer.observe(element);
        });
    }

    getResizeObservable(): Observable<void> {
        return this._resizeSubject.asObservable().pipe(
            skip(1),
        );
    }

    getCommonFontSize(): Observable<string> {
        return this._commonFontSize.asObservable().pipe(
            filter(fontSize => fontSize !== ''),
            distinctUntilChanged(),
        );
    }

    getSopoPopupFontSize(): Observable<string> {
        return this._sopoPopopFontSize.asObservable().pipe(
            filter(fontSize => fontSize !== ''),
            distinctUntilChanged(),
        );
    }

    getTournamentRegistrationPageFontSize(): Observable<string> {
        return this._tournamentRegPageFontSize.asObservable().pipe(
            filter(fontSize => fontSize !== ''),
            distinctUntilChanged(),
        );
    }

    getHeadeFontSize(): Observable<string> {
        return this._headerFontSize.asObservable().pipe(
            filter(fontSize => fontSize !== ''),
            distinctUntilChanged(),
        );
    }

    getBitcoinGameWinnerFontSize(): Observable<string> {
        return this._bitcoinGameWinnerFontSize.asObservable().pipe(
            filter(fontSize => fontSize !== ''),
            distinctUntilChanged(),
        );
    }

    getCertificateFontSize(): Observable<string> {
        return this._certificateFontSize.asObservable().pipe(
            filter(fontSize => fontSize !== ''),
            distinctUntilChanged(),
        );
    }

    getSweepstakeOveralyFontSize(): Observable<string> {
        return this._sweepstakeOverlayFontSize.asObservable().pipe(
            filter(fontSize => fontSize !== ''),
            distinctUntilChanged(),
        );
    }

    getSopoToastFontSize(): Observable<string> {
        return this._sopoToastFontSize.asObservable().pipe(
            filter(fontSize => fontSize !== ''),
            distinctUntilChanged(),
        );
    }

    manualResize() {
        this._manualResizeSubject.next();
    }

    ngOnDestroy(): void {
        if (this._resizeSubscription) this._resizeSubscription.unsubscribe();
        if (this._windowResizeSubscription) this._windowResizeSubscription.unsubscribe();
    }
}

export interface Size {
    width: number;
    height: number;
}
